import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { projectsData, certificationsData, githubReposData } from '../data/contentData';

const PERSONA_SEARCHABLE = {
  developer: ['projects', 'github', 'certifications'],
  recruiter: ['certifications'],
  athlete: [],
};


const buildSearchIndex = (persona) => [
  ...projectsData.map((item) => ({
    id: `project-${item.id}`,
    type: 'projects',
    appId: persona === 'recruiter' ? 'impact-stories' : 'ml-models',
    title: item.title,
    subtitle: item.credential || (item.deepDive?.subtitle ?? ''),
    tags: item.tags || [],
    icon: '🧠',
    // url: item.demoUrl,
  })),
  ...githubReposData.map(item => ({
    id: `repo-${item.id}`,
    type: 'github',
    appId: 'github',
    title: item.name,
    subtitle: item.description,
    tags: [item.language],
    icon: '🐙',
    url: item.url,
  })),
  ...certificationsData.map((item) => ({
    id: `cert-${item.id}`,
    type: 'certifications',
    appId: 'certifications',
    title: item.title,
    subtitle: `${item.issuer} · ${item.date}`,
    tags: [],
    icon: '🏅',
  })),
];

const TYPE_LABELS = {
  projects: 'Projects',
  github:'GitHub Repos',
  certifications: 'Certifications',
};

const SearchOverlay = ({ query, persona, onOpenApp, onClose }) => {
  const searchIndex = useMemo(() => buildSearchIndex(persona), [persona]);

  const results = useMemo(() => {
    if (!query || query.trim().length < 2) return {};

    const q = query.toLowerCase();
    const allowedTypes = PERSONA_SEARCHABLE[persona] || [];

    const matches = searchIndex.filter((item) => {
      // Must be in persona's allowed content types
      if (!allowedTypes.includes(item.type)) return false;

      // Search across title, subtitle, and tags
      return (
        item.title.toLowerCase().includes(q) ||
        item.subtitle.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    });

    // Group by type
    return matches.reduce((acc, item) => {
      if (!acc[item.type]) acc[item.type] = [];
      acc[item.type].push(item);
      return acc;
    }, {});
  }, [query, persona, searchIndex]);

  const hasResults = Object.keys(results).length > 0;
  const isTyping = query && query.trim().length >= 2;

  return (
    <AnimatePresence>
      {query && query.trim().length > 0 && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[48] bg-black/60 backdrop-blur-sm"
            style={{ top: '32px' }}
            onClick={onClose}
          />

          {/* Results Panel */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed left-0 right-0 z-[49] max-h-[70vh] overflow-y-auto"
            style={{ top: '96px' }} /* Below StatusBar (32px) + Header (~64px) */
          >
            <div className="max-w-2xl mx-auto px-4">
              <div className="bg-[#1C1C1E] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
                {isTyping && hasResults && (
                  <div className="p-2">
                    {Object.entries(results).map(([type, items]) => (
                      <div key={type}>
                        {/* Section Header */}
                        <div className="px-3 pt-3 pb-1">
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                            {TYPE_LABELS[type] || type}
                          </span>
                        </div>

                        {/* Items */}
                        {items.map((item, i) => (
                          <motion.button
                            key={item.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.03 }}
                            onClick={() => {
                              onOpenApp(item.appId);
                              onClose();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors text-left group"
                          >
                            <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                              <span className="text-lg">{item.icon}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-pure-white font-medium truncate">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {item.subtitle}
                              </p>
                            </div>
                            <svg
                              className="w-4 h-4 text-gray-600 group-hover:text-apple-blue transition-colors shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </motion.button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {isTyping && !hasResults && (
                  <div className="p-8 text-center">
                    <p className="text-gray-500 text-sm">No results for "{query}"</p>
                  </div>
                )}

                {!isTyping && (
                  <div className="p-8 text-center">
                    <p className="text-gray-500 text-sm">Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
